import React from 'react';
import RackImage from 'utils/RackImage';
import { cn } from 'shared/lib/cn';
import type { RackModel } from 'shared/lib/rackLayout';

export type RackThumbProps = {
  rack: RackModel;
  /** Pixels per rack unit. The caller picks the scale; the width is the caller's box. */
  uPx: number;
  /** "No Rack" still packs at 42U but classic drops the frame (R6). */
  framed: boolean;
  vendorId: string;
  photographic: boolean;
  onOpen: () => void;
  /** Prefix for the rack and unit test ids, so two readouts on one page stay distinct. */
  testId: string;
};

/** One rack as a small, live readout: the units where the packer put them, the whole
 *  thing a button into Rack View. Shared by the workbench's Rack section and the
 *  rail's compact rack so the two cannot draw a rack differently. */
export function RackThumb({ rack, uPx, framed, vendorId, photographic, onOpen, testId }: RackThumbProps) {
  return (
    <button
      type="button"
      data-testid={`${testId}-rack-${rack.index}`}
      data-framed={framed ? 'true' : 'false'}
      aria-label={`Open Rack View, rack ${rack.index + 1}`}
      onClick={onOpen}
      className={cn(
        'relative w-full cursor-zoom-in rounded-[2px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60',
        framed && 'bg-surface-raised ring-1 ring-inset ring-hairline/25',
      )}
      style={{ height: rack.size * uPx }}
    >
      {rack.units.map(({ position, unit }) => (
        // Slot index 0 is the TOP of the rack (shared/lib/rackLayout).
        <span
          key={`${rack.index}-${position}`}
          data-testid={`${testId}-unit-${unit.label.replace(/\s+/g, '-')}`}
          title={unit.jbodLabel ? `${unit.label}, ${unit.jbodLabel}` : unit.label}
          className={cn('absolute inset-x-0 block overflow-hidden', photographic && 'rack-unit-photo bg-surface-high')}
          style={{ top: position * uPx, height: unit.heightU * uPx }}
        >
          <RackImage type={unit.type} vendor={vendorId} />
        </span>
      ))}
    </button>
  );
}
